import { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { db } from '../utils/firebase';
import { enrichBeerReviewDoc, fetchBeerName } from '../utils/enrichBeerReviewDocs';

/**
 * Fetches beer reviews scoped to a single beer, with lazy execution via enabled flag.
 * @param {object} params
 * @param {string} [params.beerId]
 * @param {boolean} [params.enabled]
 * @param {number} [params.reviewLimit]
 * @returns {{ reviews: object[], loading: boolean, error: string | null }}
 */
function useBeerReviewsForBeer({ beerId, enabled = false, reviewLimit = 20 }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchReviews = async () => {
      if (!enabled || typeof beerId !== 'string' || beerId.length === 0) {
        if (!cancelled) {
          setReviews([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
        }

        const reviewsRef = collection(db, 'beerReview');
        const reviewsQuery = query(
          reviewsRef,
          where('beerId', '==', beerId),
          orderBy('date', 'desc'),
          limit(reviewLimit),
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);

        if (cancelled) {
          return;
        }

        if (reviewsSnapshot.empty) {
          setReviews([]);
          return;
        }

        const beerName = await fetchBeerName(beerId);

        const reviewsData = await Promise.all(
          reviewsSnapshot.docs.map((reviewDoc) => enrichBeerReviewDoc(reviewDoc, beerName)),
        );

        if (!cancelled) {
          setReviews(reviewsData);
        }
      } catch (fetchError) {
        console.error('Error fetching beer reviews for beer:', fetchError);
        if (!cancelled) {
          setError(fetchError.message);
          setReviews([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReviews();

    return () => {
      cancelled = true;
    };
  }, [beerId, enabled, reviewLimit]);

  return { reviews, loading, error };
}

export default useBeerReviewsForBeer;
